import { useEffect, useState } from "react";
import { HttpError, request } from "../../../api/http";
import { ResizeHandle, useResizableDrawer } from "../../../hooks/useResizableDrawer";
import { BrokerDetail } from "../../../types/brokers";

type Props = {
  broker: BrokerDetail;
  token: string | null;
  onClose: () => void;
  onSaved: () => void;
};

type Form = {
  businessName: string;
  brokerEmail: string;
  brokerPhone: string;
  address: string;
  complementaryUrl: string;
  medorCode: string;
  themeColor: string;
  externalId: string;
  htmlCode: string;
  markets: string[];
};

const SUGGESTED_MARKETS = ["ES", "FR", "IT", "AXA_WL", "SQL_ES", "AXA_WL_ES"];

function toForm(broker: BrokerDetail): Form {
  return {
    businessName: broker.businessName ?? "",
    brokerEmail: broker.brokerEmail ?? "",
    brokerPhone: broker.brokerPhone ?? "",
    address: broker.address ?? "",
    complementaryUrl: broker.complementaryUrl ?? "",
    medorCode: broker.medorCode ?? "",
    themeColor: broker.themeColor ?? "",
    externalId: broker.externalId ?? "",
    htmlCode: broker.htmlCode ?? "",
    markets: [...broker.markets],
  };
}

export default function EditBrokerDrawer({
  broker,
  token,
  onClose,
  onSaved,
}: Props) {
  const { width, startResize } = useResizableDrawer({ initial: 560, min: 380 });
  const [form, setForm] = useState<Form>(() => toForm(broker));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [marketInput, setMarketInput] = useState("");

  useEffect(() => {
    // Reset internal state when the broker we're editing changes.
    setForm(toForm(broker));
  }, [broker]);

  const set = <K extends keyof Form>(key: K, value: Form[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const toggleMarket = (marketToken: string) => {
    const upper = marketToken.toUpperCase().trim();
    if (!upper) return;
    setForm((prev) => ({
      ...prev,
      markets: prev.markets.includes(upper)
        ? prev.markets.filter((m) => m !== upper)
        : [...prev.markets, upper],
    }));
  };

  const addCustomMarket = () => {
    const marketToken = marketInput.toUpperCase().trim();
    if (!marketToken) return;
    if (!/^[A-Z0-9_]{2,16}$/.test(marketToken)) {
      setError("Token de mercado inválido (A-Z 0-9 _ 2–16 chars)");
      return;
    }
    if (!form.markets.includes(marketToken)) {
      setForm((prev) => ({ ...prev, markets: [...prev.markets, marketToken] }));
    }
    setMarketInput("");
  };

  // Build the PATCH payload: only fields that differ from the original
  // broker get included, so the backend's column-level whitelist gets a
  // tight diff and unchanged fields are left intact.
  const buildPayload = (): Record<string, unknown> => {
    const payload: Record<string, unknown> = {};
    const original = toForm(broker);

    const stringFields: (keyof Form)[] = [
      "businessName",
      "brokerEmail",
      "brokerPhone",
      "address",
      "complementaryUrl",
      "medorCode",
      "themeColor",
      "externalId",
      "htmlCode",
    ];
    stringFields.forEach((f) => {
      const next = (form[f] as string).trim();
      const prev = (original[f] as string).trim();
      if (next !== prev) {
        payload[f] = next === "" ? null : next;
      }
    });

    // Markets: compare as set, send if differs.
    const sortedNext = [...form.markets].sort().join(",");
    const sortedPrev = [...original.markets].sort().join(",");
    if (sortedNext !== sortedPrev) {
      payload.markets = form.markets;
    }

    return payload;
  };

  const submit = async () => {
    setError(null);
    const payload = buildPayload();
    if (Object.keys(payload).length === 0) {
      setError("No hay cambios para guardar");
      return;
    }
    setSubmitting(true);
    try {
      await request(`/api/admin/operations/brokers/${broker.brokerInternalId}`, {
        method: "PATCH",
        token,
        body: payload,
      });
      onSaved();
    } catch (err) {
      if (err instanceof HttpError) {
        const body = err.body as
          | { error?: string; field?: string; reason?: string; message?: string }
          | undefined;
        if (body?.error === "INVALID_FIELD" && body.field) {
          setError(`Campo inválido: ${body.field} (${body.reason ?? "?"})`);
        } else {
          setError(body?.message ?? body?.error ?? `HTTP ${err.status}`);
        }
      } else {
        setError("No se pudo guardar");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={overlay}>
      <aside style={{ ...drawer, width }}>
        <ResizeHandle onMouseDown={startResize} />
        <header style={drawerHeader}>
          <h2 style={{ margin: 0, fontSize: "1rem", color: "#1f2933" }}>
            Editar broker #{broker.brokerInternalId}
          </h2>
          <button type="button" onClick={onClose} style={closeBtn}>
            ✕
          </button>
        </header>

        <div style={drawerBody}>
          <Section title="Contacto">
            <Field label="Razón social">
              <input
                type="text"
                value={form.businessName}
                onChange={(e) => set("businessName", e.target.value)}
                style={inputStyle}
                maxLength={255}
              />
            </Field>
            <Field label="Email">
              <input
                type="email"
                value={form.brokerEmail}
                onChange={(e) => set("brokerEmail", e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="Teléfono">
              <input
                type="tel"
                value={form.brokerPhone}
                onChange={(e) =>
                  set("brokerPhone", e.target.value.replace(/[^0-9]/g, ""))
                }
                style={inputStyle}
                placeholder="Solo dígitos"
              />
            </Field>
            <Field label="Dirección">
              <input
                type="text"
                value={form.address}
                onChange={(e) => set("address", e.target.value)}
                style={inputStyle}
                maxLength={255}
              />
            </Field>
            <Field label="URL complementaria">
              <input
                type="url"
                value={form.complementaryUrl}
                onChange={(e) => set("complementaryUrl", e.target.value)}
                style={inputStyle}
                placeholder="https://..."
                maxLength={255}
              />
            </Field>
          </Section>

          <Section title="Comercial">
            <Field label="Código MEDOR (AXA)">
              <input
                type="text"
                value={form.medorCode}
                onChange={(e) =>
                  set(
                    "medorCode",
                    e.target.value
                      .toUpperCase()
                      .replace(/[^A-Z0-9_-]/g, ""),
                  )
                }
                style={{ ...inputStyle, fontFamily: "monospace" }}
                maxLength={32}
                placeholder="755332"
              />
            </Field>
            <Field label="External ID">
              <input
                type="text"
                value={form.externalId}
                onChange={(e) => set("externalId", e.target.value)}
                style={inputStyle}
                maxLength={255}
              />
            </Field>
          </Section>

          <Section title="Branding">
            <Field label="Color tema">
              <div
                style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}
              >
                <input
                  type="color"
                  value={
                    /^#[0-9a-fA-F]{6}$/.test(form.themeColor)
                      ? form.themeColor
                      : "#0069d9"
                  }
                  onChange={(e) => set("themeColor", e.target.value)}
                  style={{
                    width: 40,
                    height: 32,
                    padding: 0,
                    border: "1px solid #dee2e6",
                    borderRadius: "0.4rem",
                    background: "white",
                    cursor: "pointer",
                  }}
                />
                <input
                  type="text"
                  value={form.themeColor}
                  onChange={(e) => set("themeColor", e.target.value)}
                  style={{ ...inputStyle, flex: 1, fontFamily: "monospace" }}
                  placeholder="#0069d9"
                />
                {form.themeColor && (
                  <button
                    type="button"
                    onClick={() => set("themeColor", "")}
                    style={ghostBtnStyle}
                  >
                    Limpiar
                  </button>
                )}
              </div>
            </Field>
            <Field label="HTML embebido">
              <textarea
                value={form.htmlCode}
                onChange={(e) => set("htmlCode", e.target.value)}
                style={{
                  ...inputStyle,
                  minHeight: 80,
                  fontFamily: "monospace",
                  fontSize: ".75rem",
                  resize: "vertical",
                }}
                placeholder="<script>...</script>"
              />
            </Field>
          </Section>

          <Section title="Mercados">
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
              {SUGGESTED_MARKETS.map((m) => {
                const active = form.markets.includes(m);
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => toggleMarket(m)}
                    style={{
                      ...chipStyle,
                      ...(active
                        ? {
                            background: "#cfe2ff",
                            color: "#084298",
                            border: "1px solid #b6d4fe",
                          }
                        : {}),
                    }}
                  >
                    {m}
                  </button>
                );
              })}
              {form.markets
                .filter((m) => !SUGGESTED_MARKETS.includes(m))
                .map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => toggleMarket(m)}
                    style={{
                      ...chipStyle,
                      background: "#e2d9f3",
                      color: "#4a2c8c",
                      border: "1px solid #c4b0e0",
                    }}
                    title="Pulsa para quitar"
                  >
                    {m} ✕
                  </button>
                ))}
            </div>
            <div
              style={{
                display: "flex",
                gap: "0.4rem",
                alignItems: "center",
                marginTop: "0.5rem",
              }}
            >
              <input
                type="text"
                value={marketInput}
                onChange={(e) => setMarketInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addCustomMarket();
                  }
                }}
                style={{ ...inputStyle, flex: 1 }}
                placeholder="Añadir mercado personalizado (ej. SQL_ES)"
              />
              <button
                type="button"
                onClick={addCustomMarket}
                style={ghostBtnStyle}
              >
                Añadir
              </button>
            </div>
          </Section>

          {error && (
            <div
              style={{
                background: "#fde2e2",
                color: "#b32424",
                padding: "0.5rem 0.7rem",
                borderRadius: "0.4rem",
                fontSize: ".82rem",
              }}
            >
              {error}
            </div>
          )}
        </div>

        <footer style={drawerFooter}>
          <button
            type="button"
            onClick={onClose}
            style={ghostBtnStyle}
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            style={primaryBtnStyle}
            disabled={submitting}
          >
            {submitting ? "Guardando…" : "Guardar cambios"}
          </button>
        </footer>
      </aside>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
      <h3
        style={{
          margin: 0,
          fontSize: ".72rem",
          textTransform: "uppercase",
          letterSpacing: ".3px",
          color: "#73879C",
          fontWeight: 700,
          borderBottom: "2px solid #0069d9",
          paddingBottom: "0.25rem",
        }}
      >
        {title}
      </h3>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        {children}
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
      <label
        style={{ fontSize: ".75rem", color: "#73879C", fontWeight: 600 }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.35)",
  display: "flex",
  justifyContent: "flex-end",
  zIndex: 100,
};

const drawer: React.CSSProperties = {
  background: "white",
  boxShadow: "-10px 0 30px rgba(0,0,0,.25)",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  height: "100vh",
};

const drawerHeader: React.CSSProperties = {
  padding: "0.9rem 1.1rem",
  borderBottom: "1px solid #e9ecef",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const closeBtn: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "#73879C",
  fontSize: "1rem",
  cursor: "pointer",
  padding: "0.2rem 0.4rem",
};

const drawerBody: React.CSSProperties = {
  padding: "1rem 1.1rem",
  display: "flex",
  flexDirection: "column",
  gap: "1.1rem",
  overflowY: "auto",
  flex: 1,
};

const drawerFooter: React.CSSProperties = {
  padding: "0.7rem 1.1rem",
  borderTop: "1px solid #e9ecef",
  display: "flex",
  gap: "0.5rem",
  justifyContent: "flex-end",
};

const inputStyle: React.CSSProperties = {
  padding: "0.4rem 0.55rem",
  borderRadius: "0.4rem",
  border: "1px solid #dee2e6",
  background: "#f8f9fb",
  fontSize: ".82rem",
  color: "#1f2933",
  width: "100%",
  boxSizing: "border-box",
};

const chipStyle: React.CSSProperties = {
  padding: "0.25rem 0.55rem",
  borderRadius: "0.35rem",
  fontSize: ".72rem",
  fontWeight: 600,
  border: "1px solid #dee2e6",
  background: "white",
  color: "#73879C",
  cursor: "pointer",
  fontFamily: "monospace",
};

const primaryBtnStyle: React.CSSProperties = {
  padding: "0.45rem 0.9rem",
  borderRadius: "0.4rem",
  background: "#0069d9",
  color: "white",
  border: "1px solid #0069d9",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: "pointer",
};

const ghostBtnStyle: React.CSSProperties = {
  padding: "0.4rem 0.7rem",
  borderRadius: "0.4rem",
  background: "white",
  color: "#1f2933",
  border: "1px solid #dee2e6",
  fontSize: ".78rem",
  fontWeight: 600,
  cursor: "pointer",
};
